import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import RequireMenuAccess from '../common/RequireMenuAccess';
import RTButton from '../common/RTButton';
import Table from '../common/Table';
import RTFilters from '../common/RTFilters';
import RTFilteredTable from '../common/RTFilteredTable';
import AddUserModal from './AddUserModal';
import { apiGet, apiPost } from '../../api/apiClient';
import { toastConfirm, toastError, toastSuccess } from '../common/toast';
import '../common/rtCommon.css';
import './ManageUsers.css';
import RTPageLayout from '../common/RTPageLayout';

const PERM = 'access-control.manage_users';

const getDefaultFilters = () => ({
  search: '',
  include_inactive: false,
  not_logged_in: false,
});

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-GB');
  } catch {
    return String(value);
  }
};

const ManageUsers = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState('pg');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(getDefaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(getDefaultFilters);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const filterFields = useMemo(
    () => [
      {
        key: 'search',
        label: 'Search',
        type: 'text',
        width: 'wide',
        placeholder: 'Username or mobile…',
        applyImmediately: false,
      },
      {
        key: 'include_inactive',
        label: 'Status',
        type: 'checkbox',
        checkboxText: 'Show Inactive Users',
        width: 'compact',
        applyImmediately: true,
      },
      {
        key: 'not_logged_in',
        label: 'Login',
        type: 'checkbox',
        checkboxText: 'Not Logged In',
        width: 'compact',
        applyImmediately: true,
      },
    ],
    []
  );

  const loadUsers = useCallback(async (filterParams = appliedFilters, tab = activeTab) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        kind: tab === 'pg' ? 'pg' : 'management',
        include_inactive: String(Boolean(filterParams.include_inactive)),
        not_logged_in: String(Boolean(filterParams.not_logged_in)),
      });
      if (filterParams.search?.trim()) {
        params.set('q', filterParams.search.trim());
      }
      const res = await apiGet(`/auth-users?${params.toString()}`);
      const nextUsers = res.data?.users || [];
      setUsers(nextUsers);
      setSelectedUserId((prev) =>
        nextUsers.some((u) => u.id === prev) ? prev : null
      );
    } catch (err) {
      toastError(err?.response?.data?.detail || 'Failed to load users');
      setUsers([]);
      setSelectedUserId(null);
    } finally {
      setLoading(false);
    }
  }, [activeTab, appliedFilters]);

  useEffect(() => {
    loadUsers(appliedFilters, activeTab);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (key, value, { applyImmediately } = {}) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    if (applyImmediately) {
      setAppliedFilters(next);
      loadUsers(next, activeTab);
    }
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    loadUsers(filters, activeTab);
  };

  const handleClearFilters = () => {
    const defaults = getDefaultFilters();
    setFilters(defaults);
    setAppliedFilters(defaults);
    loadUsers(defaults, activeTab);
  };

  const appliedFilterPills = useMemo(() => {
    const pills = [];
    if (appliedFilters.search?.trim()) {
      pills.push({
        key: 'search',
        label: `Search: ${appliedFilters.search.trim()}`,
      });
    }
    if (appliedFilters.include_inactive) {
      pills.push({ key: 'include_inactive', label: 'Show Inactive Users' });
    }
    if (appliedFilters.not_logged_in) {
      pills.push({ key: 'not_logged_in', label: 'Not Logged In' });
    }
    return pills;
  }, [appliedFilters]);

  const handleRemoveAppliedFilter = (key) => {
    const next = { ...appliedFilters };
    if (key === 'include_inactive' || key === 'not_logged_in') next[key] = false;
    else next[key] = '';
    setFilters(next);
    setAppliedFilters(next);
    loadUsers(next, activeTab);
  };

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setSelectedUserId(null);
    setActiveTab(tab);
  };

  const selectedUser = useMemo(
    () => users.find((u) => u.id === selectedUserId) || null,
    [users, selectedUserId]
  );

  const handleDeleteUser = async () => {
    if (!selectedUser) {
      toastError('Select a user to delete');
      return;
    }
    const isPlatformAdmin =
      selectedUser.username === 'rtadmin' ||
      selectedUser.role_code === 'RT_Admin';
    if (isPlatformAdmin) {
      toastError('Platform admin account cannot be deleted');
      return;
    }
    const confirmed = await toastConfirm(
      true,
      `Delete user "${selectedUser.username}"? This cannot be undone.`
    );
    if (!confirmed) return;
    try {
      const res = await apiPost(`/auth-users/${selectedUser.id}/delete`);
      toastSuccess(res.data?.message || 'User deleted');
      setSelectedUserId(null);
      loadUsers(appliedFilters, activeTab);
    } catch (err) {
      toastError(err?.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async (user) => {
    const confirmed = await toastConfirm(
      true,
      `Reset password for "${user.username}" to the default password? They will be required to change it on next login.`
    );
    if (!confirmed) return;
    try {
      const res = await apiPost(`/auth-users/${user.id}/reset-password`);
      toastSuccess(res.data?.message || 'Password reset');
      loadUsers(appliedFilters, activeTab);
    } catch (err) {
      toastError(err?.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleToggleActive = async (user) => {
    const activate = !user.is_active;
    const message = activate
      ? `Activate "${user.username}"? Password will be reset to default and they must change it on next login.`
      : `Deactivate "${user.username}"? They will no longer be able to log in.`;
    const confirmed = await toastConfirm(true, message);
    if (!confirmed) return;
    try {
      const res = await apiPost(`/auth-users/${user.id}/status`, {
        is_active: activate,
      });
      toastSuccess(
        res.data?.message || (activate ? 'User activated' : 'User deactivated')
      );
      loadUsers(appliedFilters, activeTab);
    } catch (err) {
      toastError(err?.response?.data?.detail || 'Failed to update user status');
    }
  };

  const columns = useMemo(
    () => [
      ...(activeTab === 'management'
        ? [
            {
              Header: '',
              accessor: 'select',
              disableExport: true,
              width: 40,
              Cell: ({ row }) => {
                const user = row;
                const isPlatformAdmin =
                  user.username === 'rtadmin' || user.role_code === 'RT_Admin';
                return (
                  <input
                    type="radio"
                    name="manage-users-select"
                    className="manage-users-select"
                    checked={selectedUserId === user.id}
                    disabled={isPlatformAdmin}
                    onChange={() => setSelectedUserId(user.id)}
                    aria-label={`Select ${user.username}`}
                  />
                );
              },
            },
          ]
        : []),
      { Header: 'Username', accessor: 'username' },
      {
        Header: 'Mobile',
        accessor: 'mobile',
        Cell: ({ value }) => value || '—',
      },
      { Header: 'Role', accessor: 'role_name' },
      {
        Header: activeTab === 'pg' ? 'PG Name' : 'Staff',
        accessor: activeTab === 'pg' ? 'pg_name' : 'staff_name',
        Cell: ({ value }) => value || '—',
      },
      {
        Header: 'Status',
        accessor: 'is_active',
        Cell: ({ value }) => (
          <span
            className={`rt-status-pill ${value ? 'rt-status-active' : 'rt-status-vacated'
              }`}
          >
            {value ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        Header: 'Logged in',
        accessor: 'has_logged_in',
        Cell: ({ value }) => (value ? 'Yes' : 'No'),
      },
      {
        Header: 'Last login',
        accessor: 'last_login_at',
        Cell: ({ value }) => formatDateTime(value),
      },
      {
        Header: 'Password reset',
        accessor: 'password_reset_at',
        Cell: ({ value }) => formatDateTime(value),
      },
      {
        Header: 'Failed logins',
        accessor: 'failed_login_count',
        Cell: ({ value }) => (value == null ? 0 : value),
      },
      {
        Header: 'Actions',
        accessor: 'actions',
        disableExport: true,
        Cell: ({ row }) => {
          const user = row;
          const isPlatformAdmin =
            user.username === 'rtadmin' || user.role_code === 'RT_Admin';
          return (
            <div className="manage-users-actions">
              <button
                type="button"
                className="manage-users-action-link"
                onClick={() => handleResetPassword(user)}
              >
                Reset password
              </button>
              {isPlatformAdmin && user.is_active ? null : (
                <button
                  type="button"
                  className={`manage-users-action-link ${user.is_active ? 'manage-users-action-warn' : ''
                    }`}
                  onClick={() => handleToggleActive(user)}
                >
                  {user.is_active ? 'Deactivate' : 'Activate'}
                </button>
              )}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTab, appliedFilters, selectedUserId]
  );

  return (
    <RequireMenuAccess>
      <RTPageLayout
        activePath={location.pathname}
        title="Manage Users"
        actions={<>
          {activeTab === 'management' ? (
            <>
              <RTButton
                variant="danger"
                permissionKey={PERM}
                onClick={handleDeleteUser}
                disabled={!selectedUser}
              >
                Delete User
              </RTButton>
              <RTButton permissionKey={PERM} onClick={() => setShowAdd(true)}>
                Add User
              </RTButton>
            </>
          ) : null}
          <RTButton
            variant="outline-primary"
            size="sm"
            onClick={() => navigate('/access-control')}
          >
            ← Back
          </RTButton>
        </>}
      >



        <div className="rt-section" style={{ flex: 'none' }}>
          <RTFilters
            fields={filterFields}
            values={filters}
            onChange={handleFilterChange}
            onApply={handleApplyFilters}
            pills={appliedFilterPills}
            onRemovePill={handleRemoveAppliedFilter}
            onClearAll={handleClearFilters}
          />
        </div>

        <div className="rt-main-section">
          <div className="manage-users-tabs">
            <button
              type="button"
              className={`manage-users-tab ${activeTab === 'pg' ? 'active' : ''}`}
              onClick={() => handleTabChange('pg')}
            >
              PG Users
            </button>
            <button
              type="button"
              className={`manage-users-tab ${activeTab === 'management' ? 'active' : ''
                }`}
              onClick={() => handleTabChange('management')}
            >
              Management Users
            </button>
          </div>

          <RTFilteredTable
            loading={loading}
            loadingMessage="Loading users…"
          >
            <Table
              columns={columns}
              data={users}
              showSearch={false}
              showPagination
              exportable={false}
              noDataText={
                activeTab === 'pg'
                  ? 'No PG users found'
                  : 'No management users found'
              }
            />
          </RTFilteredTable>
        </div>
      </RTPageLayout>

      <AddUserModal
        isOpen={showAdd}
        onClose={() => setShowAdd(false)}
        onCreated={() => loadUsers(appliedFilters, activeTab)}
      />
    </RequireMenuAccess>
  );
};

export default ManageUsers;
